import React, { useState } from 'react';
import { Box, Paper, Typography, TextField, InputAdornment, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { groupByCategory, LIB_PREFIX, LIBRARY_ID } from './planUtils';

const FeatureBlock = ({ feature, inPlan, onAdd, t }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `${LIB_PREFIX}${feature.key}`, disabled: inPlan, data: { featureKey: feature.key } });
  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      data-testid={`feature-block-${feature.key}`}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, mb: 0.75,
        opacity: inPlan ? 0.55 : isDragging ? 0.4 : 1,
        cursor: inPlan ? 'default' : 'grab',
        borderStyle: isDragging ? 'dashed' : 'solid',
      }}
    >
      {!inPlan && (
        <Box
          {...attributes}
          {...listeners}
          role="button"
          aria-roledescription="draggable feature"
          aria-label={t('Drag {{name}} into the plan', { name: feature.name, defaultValue: `Drag ${feature.name} into the plan` })}
          sx={{ display: 'flex', color: 'text.disabled', cursor: 'grab', touchAction: 'none' }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap>{feature.name}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{feature.description}</Typography>
      </Box>
      {inPlan ? (
        <Typography variant="caption" color="success.main" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
          <CheckIcon sx={{ fontSize: 14 }} /> {t('In plan', { defaultValue: 'In plan' })}
        </Typography>
      ) : (
        <Button size="small" variant="text" onClick={() => onAdd(feature.key)} aria-label={t('Add {{name}}', { name: feature.name, defaultValue: `Add ${feature.name}` })}>
          {t('Add', { defaultValue: 'Add' })}
        </Button>
      )}
    </Paper>
  );
};

// Left pane of the builder: every registry feature, grouped by category,
// searchable. Also a drop target so dragging a card back here removes it.
const FeatureLibrary = ({ features, inPlanKeys, onAdd, t }) => {
  const [query, setQuery] = useState('');
  const { setNodeRef, isOver } = useDroppable({ id: LIBRARY_ID });
  const q = query.trim().toLowerCase();
  const visible = q ? features.filter((f) => `${f.name} ${f.description} ${f.category}`.toLowerCase().includes(q)) : features;
  const groups = groupByCategory(visible);

  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      sx={{ p: 1.5, bgcolor: 'background.default', borderColor: isOver ? 'error.main' : 'divider', height: '100%' }}
    >
      <Typography variant="overline" color="text.secondary">{t('Feature library', { defaultValue: 'Feature library' })}</Typography>
      <TextField
        size="small"
        fullWidth
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('Search features', { defaultValue: 'Search features' })}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        sx={{ my: 1 }}
      />
      {isOver && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
          {t('Drop to remove from the plan', { defaultValue: 'Drop to remove from the plan' })}
        </Typography>
      )}
      {groups.map((group) => (
        <Box key={group.category} sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 600 }}>
            {t(group.category, { defaultValue: group.category })}
          </Typography>
          {group.items.map((feature) => (
            <FeatureBlock key={feature.key} feature={feature} inPlan={inPlanKeys.has(feature.key)} onAdd={onAdd} t={t} />
          ))}
        </Box>
      ))}
      {groups.length === 0 && (
        <Typography variant="body2" color="text.secondary">{t('No features match', { defaultValue: 'No features match' })}</Typography>
      )}
    </Paper>
  );
};

export default FeatureLibrary;
